import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { AlertTriangle, User, Fuel, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Red Flags Overview (renamed from "Fraud Dashboard").
 *
 * Groups every open + recent flag into three buckets:
 *   1. WHO — counts + ₹ impact per cashier / filler (so the manager can see
 *      exactly whose shifts keep triggering issues)
 *   2. WHERE — nozzles / equipment that repeat
 *   3. WHEN — shift × cashier matrix showing cash-difference concentration
 *
 * Every row drills into the underlying entries.
 */
export default function FraudDashboard() {
  const [data, setData] = useState(null);
  const [byPerson, setByPerson] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    (async () => {
      const [r, bp] = await Promise.all([
        api.get("/flags/fraud/summary", { params: { days } }),
        api.get("/flags/by_person", { params: { days } }).catch(() => ({ data: { people: [] } })),
      ]);
      setData(r.data);
      setByPerson(bp.data);
    })();
  }, [days]);

  return (
    <div className="space-y-4" data-testid="fraud-root">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <AlertTriangle className="text-amber-600" size={28} /> Red Flags Overview
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Where is the money going? Buckets by who, where and when — sorted by ₹ impact.
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setDays(n)}
              data-testid={`fraud-days-${n}`}
              className={
                "px-3 h-10 border-2 font-bold text-sm rounded-sm " +
                (days === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")
              }
            >
              {n}d
            </button>
          ))}
        </div>
      </div>

      {/* Bucket 1 — WHO (person rollup with severity) */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm" data-testid="flags-by-person-card">
        <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
          <User size={16} /> By person — full rollup of open flags per staff member
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          Every attendee and cashier tagged in at least one open red flag over the last {days} days. Sorted by ₹ impact desc. Managers use this to have specific 1-on-1s.
        </p>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Person</th>
                <th className="text-right">Flags</th>
                <th className="text-right">HIGH</th>
                <th className="text-right">MED</th>
                <th className="text-right">LOW</th>
                <th className="text-right">Total ₹ impact</th>
                <th className="text-left">Top themes</th>
              </tr>
            </thead>
            <tbody>
              {(byPerson?.people || []).map((p) => (
                <tr key={p.person} data-testid={`person-row-${p.person.replace(/\s+/g, "-")}`}>
                  <td className="font-bold">{p.person}</td>
                  <td className="num">{p.count}</td>
                  <td className={"num font-bold " + (p.high > 0 ? "text-red-600" : "text-slate-500")}>{p.high}</td>
                  <td className="num">{p.med}</td>
                  <td className="num text-slate-500">{p.low}</td>
                  <td className="num font-bold text-red-600">{fmtINR(p.total_impact)}</td>
                  <td className="text-xs">
                    {Object.entries(p.by_type || {}).slice(0, 4).map(([k, v]) => (
                      <span key={k} className="inline-block mr-1 mb-1 px-1.5 py-0.5 border border-slate-300 bg-slate-50 rounded-sm">
                        {k.replace(/_/g, " ").toLowerCase()} ({v})
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
              {!(byPerson?.people || []).length && (
                <tr>
                  <td colSpan={7} className="text-center text-slate-500 py-4">
                    No open red flags in the last {days} days — clean books.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Bucket 1b — WHO (legacy actor-only summary, kept for comparison) */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
          <User size={16} /> By actor — the cashier/actor who owns the shift
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          How many red flags each person triggered in the last {days} days. Sort by ₹ impact to spot the
          most costly patterns quickly.
        </p>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Person</th>
                <th className="text-right">Flags</th>
                <th className="text-right">Impact ₹</th>
                <th className="text-left">Recurring themes</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_actor || []).map((a) => (
                <tr key={a.actor}>
                  <td className="font-bold">{a.actor}</td>
                  <td className="num">{a.count}</td>
                  <td className="num font-bold text-red-600">{fmtINR(a.total_impact)}</td>
                  <td className="text-xs">
                    {Object.entries(a.types || {}).map(([k, v]) => (
                      <span
                        key={k}
                        className="inline-block mr-1 mb-1 px-1.5 py-0.5 border border-slate-300 bg-slate-50 rounded-sm"
                      >
                        {k === "MISSED_ITEM_HINT" ? "Missed item hint" : k.replace(/_/g, " ").toLowerCase()} ({v})
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
              {!(data?.by_actor || []).length && (
                <tr>
                  <td colSpan={4} className="text-center text-slate-500 py-4">
                    No red flags in the last {days} days — clean books.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Bucket 2 — WHERE */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
          <Fuel size={16} /> By nozzle — hardware &amp; adjustment heat
        </h3>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Nozzle</th>
                <th className="text-right">Flags</th>
                <th className="text-right">Impact ₹</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_nozzle || []).map((n) => (
                <tr key={n.nozzle_id}>
                  <td>{n.nozzle_id}</td>
                  <td className="num">{n.count}</td>
                  <td className="num font-bold text-red-600">{fmtINR(n.total_impact)}</td>
                </tr>
              ))}
              {!(data?.by_nozzle || []).length && (
                <tr>
                  <td colSpan={3} className="text-center text-slate-500 py-4">
                    No nozzle-related flags in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Bucket 3 — WHEN */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2">
          <ClipboardList size={16} /> Shift × cashier — cash-difference matrix
        </h3>
        <p className="text-xs text-slate-500 mb-2">
          Highlights recurring shortages / excesses by shift-and-cashier combo. Anything above ₹100 is red.
        </p>
        <div className="overflow-x-auto">
          <table className="dense-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Cashier</th>
                <th className="text-left">Shift</th>
                <th className="text-right">Entries</th>
                <th className="text-right">Total ₹ diff</th>
              </tr>
            </thead>
            <tbody>
              {(data?.matrix || []).map((m) => (
                <tr key={`${m.cashier}-${m.shift}`}>
                  <td>{m.cashier}</td>
                  <td>{m.shift}</td>
                  <td className="num">{m.count}</td>
                  <td
                    className={
                      "num font-bold " +
                      (Math.abs(m.total_diff) > 100 ? "text-red-600" : "text-slate-700")
                    }
                  >
                    {fmtINR(m.total_diff)}
                  </td>
                </tr>
              ))}
              {!(data?.matrix || []).length && (
                <tr>
                  <td colSpan={4} className="text-center text-slate-500 py-4">
                    No cash differences recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-xs text-slate-500">
        Need to close a flag with a comment? Open the{" "}
        <Link to="/flags" className="underline">
          Red Flags Queue
        </Link>{" "}
        to review, acknowledge or resolve individual items.
      </div>
    </div>
  );
}
